import React from 'react';
import { XIcon, UserPlusIcon, CalendarIcon, CheckCircleIcon } from 'lucide-react';
interface WelcomeOnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSkip: () => void;
  onAddStudent: () => void;
  onScheduleLesson: () => void;
  darkMode: boolean;
  hasStudents?: boolean;
  hasLessons?: boolean;
}
export const WelcomeOnboardingModal: React.FC<WelcomeOnboardingModalProps> = ({
  isOpen,
  onClose,
  onSkip,
  onAddStudent,
  onScheduleLesson,
  darkMode,
  hasStudents = false,
  hasLessons = false
}) => {
  if (!isOpen) return null;
  const studentCompleted = hasStudents;
  const lessonCompleted = hasLessons;
  // Determine which card is the "next step" to highlight
  const highlightStudent = !studentCompleted;
  const highlightLesson = studentCompleted && !lessonCompleted;
  const getCardClasses = (completed: boolean, highlight: boolean, accentColor: 'blue' | 'green') => {
    const colors = {
      blue: {
        highlightBorder: darkMode ? 'border-blue-500' : 'border-blue-500',
        highlightBg: darkMode ? 'bg-blue-500/5' : 'bg-blue-50',
        hoverBorder: 'hover:border-blue-500'
      },
      green: {
        highlightBorder: darkMode ? 'border-[#10B981]' : 'border-[#10B981]',
        highlightBg: darkMode ? 'bg-[#10B981]/5' : 'bg-emerald-50',
        hoverBorder: 'hover:border-[#10B981]'
      }
    };
    const c = colors[accentColor];
    if (completed) {
      // Completed: subtle, reduced emphasis
      return darkMode ? `border-gray-700 bg-gray-800/30 opacity-75 hover:opacity-90 hover:border-gray-600` : `border-gray-200 bg-gray-50/70 opacity-75 hover:opacity-90 hover:border-gray-300`;
    }
    if (highlight) {
      // Next step: persistent highlight border + subtle bg tint
      return darkMode ? `${c.highlightBorder} ${c.highlightBg} hover:bg-gray-800` : `${c.highlightBorder} ${c.highlightBg} hover:shadow-md`;
    }
    // Default: neutral state (not highlighted, not completed) — no accent color
    return darkMode ? `border-gray-700 bg-gray-800/50 hover:border-gray-600 hover:bg-gray-800` : `border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-white hover:shadow-md`;
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-id="element-2560">
      <div className={`relative w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden ${darkMode ? 'bg-[#1E293B] text-white' : 'bg-white text-gray-900'}`} data-id="element-2561">
        {/* Close button */}
        <button onClick={onClose} className={`absolute top-4 right-4 p-2 rounded-full transition-colors ${darkMode ? 'text-gray-400 hover:bg-gray-800 hover:text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'}`} data-id="element-2562">
          <XIcon className="w-5 h-5" data-id="element-2563" />
        </button>

        <div className="p-8 sm:p-10" data-id="element-2564">
          <div className="text-center mb-8" data-id="element-2565">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3" data-id="element-2566">
              Welcome to Maestro Connect 👋
            </h2>
            <p className={`text-lg ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-2567">
              Let's get you set up in two quick steps.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8" data-id="element-2568">
            {/* Card 1 - Add Student */}
            <div className={`relative flex flex-col items-center p-6 rounded-xl border-2 transition-all ${getCardClasses(studentCompleted, highlightStudent, 'blue')}`} data-id="element-2569">
              {studentCompleted && <div className="absolute top-3 right-3" data-id="element-2570">
                  <CheckCircleIcon className="w-5 h-5 text-[#10B981]" data-id="element-2571" />
                </div>}
              <div className={`p-4 rounded-full mb-4 ${studentCompleted ? darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-400' : darkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'}`} data-id="element-2572">
                <UserPlusIcon className="w-8 h-8" data-id="element-2573" />
              </div>
              <h3 className="text-lg font-semibold mb-4 text-center" data-id="element-2574">
                Add Your First Student
              </h3>
              <button onClick={() => {
              onAddStudent();
              onClose();
            }} className={`w-full py-2.5 px-4 font-medium rounded-lg transition-colors mt-auto ${studentCompleted ? 'bg-gray-400 hover:bg-gray-500 text-white cursor-default' : 'bg-blue-600 hover:bg-blue-700 text-white'}`} data-id="element-2575">
                {studentCompleted ? 'Done ✓' : 'Add Now'}
              </button>
            </div>

            {/* Card 2 - Schedule Lesson */}
            <div className={`relative flex flex-col items-center p-6 rounded-xl border-2 transition-all ${getCardClasses(lessonCompleted, highlightLesson, 'green')}`} data-id="element-2576">
              {lessonCompleted && <div className="absolute top-3 right-3" data-id="element-2577">
                  <CheckCircleIcon className="w-5 h-5 text-[#10B981]" data-id="element-2578" />
                </div>}
              <div className={`p-4 rounded-full mb-4 ${lessonCompleted ? darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-400' : darkMode ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#10B981]/10 text-[#10B981]'}`} data-id="element-2579">
                <CalendarIcon className="w-8 h-8" data-id="element-2580" />
              </div>
              <h3 className="text-lg font-semibold mb-4 text-center" data-id="element-2581">
                Schedule Your First Lesson
              </h3>
              <button onClick={() => {
              onScheduleLesson();
              onClose();
            }} className={`w-full py-2.5 px-4 font-medium rounded-lg transition-colors mt-auto ${lessonCompleted ? 'bg-gray-400 hover:bg-gray-500 text-white cursor-default' : 'bg-[#10B981] hover:bg-[#059669] text-white'}`} data-id="element-2582">
                {lessonCompleted ? 'Done ✓' : 'Schedule'}
              </button>
            </div>
          </div>

          <div className="text-center mt-2" data-id="element-2583">
            <button onClick={onSkip} className={`font-medium transition-colors ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`} data-id="element-2584">
              Skip for now
            </button>
          </div>
        </div>
      </div>
    </div>;
};